"use client";

import type { Article, Category } from "@/app/types/blogTypes";
import { motion } from "framer-motion";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useRef, useState, useEffect, useMemo } from "react";

type CategoriesProps = {
  categories: Category[];
  type?: "select" | "default";
  clearFilters?: boolean;
};

export function BlogCategories({
  categories,
  type = "default",
  clearFilters,
}: CategoriesProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [currentCategory, setCurrentCategory] = useState(
    searchParams.get("category") || "all"
  );

  const categoryRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const newCategory = searchParams.get("category") || "all";
    setCurrentCategory(newCategory);
  }, [searchParams]);

  const handleCategoryClick = async (category: string) => {
    const previousCategory = currentCategory;
    setCurrentCategory(category);

    const params = new URLSearchParams(searchParams.toString());
    if (category === "all") {
      params.delete("category");
    } else {
      params.set("category", category);
    }

    try {
      await router.push(`${pathname}?${params.toString()}`);
    } catch (error) {
      console.error("Erro na navegação:", error);
      setCurrentCategory(previousCategory);
    }
  };

  const filteredCategories = useMemo(() => categories, [categories]);

  if (type === "select") {
    return (
      <select
        value={currentCategory}
        onChange={(e) =>
          handleCategoryClick(
            e.target.value === "Todos" ? "all" : e.target.value
          )
        }
        className="text-sm  h-12 px-4 rounded-2xl border focus:ring-0 focus:outline-none text-gray-900 placeholder-neutral-400 "
      >
        <option value="Todos">Todas as Categorias</option>
        {categories.map((category) => (
          <option key={category.id} value={category.slug}>
            {category.name}
          </option>
        ))}
      </select>
    );
  }

  useEffect(() => {
    if (clearFilters) {
      setCurrentCategory("all");
    }
  }, [currentCategory, clearFilters]);

  return (
    <nav className="scrollbar-hidden flex w-full flex-nowrap gap-2 overflow-x-auto rounded-2xl border p-1 lg:max-w-max lg:flex-wrap lg:overflow-visible">
      {filteredCategories.map((category, index) => (
        <motion.button
          key={category.id}
          ref={(el) => {
            categoryRefs.current[index] = el;
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleCategoryClick(category.slug)}
          className={`flex-shrink-0 whitespace-nowrap rounded-xl px-4 py-2 ${
            currentCategory === category.slug
              ? "bg-[#E7EDE7BF] text-black"
              : "bg-white text-gray-500"
          }`}
        >
          {category.name}
        </motion.button>
      ))}
    </nav>
  );
}
